import type { OpenParameter } from '../../../types/apiReturnType'
import { zeroAddress } from 'viem'
import { LIQUITY_FAMILY_LENDERS } from '@1delta/lender-registry'
import { MorphoGeneralPublicResponse } from '../../../types/lender/morpho-types'
import { AdditionalYields, GenericTokenList } from '../../../types'
import { createMarketUid } from '../../../utils'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import type { LiquityMarketsRaw } from './types'

/**
 * Synthesized per-branch lender key, e.g. `LIQUITY_V2_1_1` (= mainnet wstETH
 * branch). The CHAIN ID is part of the key (Fluid convention,
 * `FLUID_<chainId>_<vaultId>`) so keys stay GLOBALLY unique for multi-chain
 * deployments (Ebisu spans Ethereum + Plasma) — labels, yield-tracer rows and
 * anything else keyed by lender_key alone would collide otherwise.
 */
export function liquityLenderKey(
  lender: string,
  chainId: string | number,
  collIndex: number,
): string {
  return `${lender}_${chainId}_${collIndex}`
}

/**
 * Recover `{ lender, chainId, collIndex }` from a per-branch key (or
 * undefined for a bare deployment key / non-family key). Fork brands share no
 * prefix, so this matches against the family list.
 */
export function liquityKeyParts(
  key: string,
): { lender: string; chainId: string; collIndex: number } | undefined {
  const base = LIQUITY_FAMILY_LENDERS.find((b) => key.startsWith(b + '_'))
  if (!base) return undefined
  const suffix = key.slice(base.length + 1)
  const m = suffix.match(/^(\d+)_(\d+)$/)
  if (!m) return undefined
  return { lender: base, chainId: m[1], collIndex: Number(m[2]) }
}

const WAD = 1e18

/**
 * Plausible upper bound for a per-UNIT collateral price in USD. Nothing listed
 * as CDP collateral trades near this (BTC ~1e5, gold ~4e3), while a mis-scaled
 * read is off by >= 1e10 — so it separates the two feed conventions cleanly.
 */
const MAX_PLAUSIBLE_UNIT_USD = 1e7

/**
 * Human USD price of a branch's collateral from its `lastGoodPrice()` read.
 *
 * The feed is WAD-USD on canonical (18-dec) collateral, but forks that list
 * NON-18-dec collateral split into two camps:
 *  - the feed stays 1e18 and the fork normalizes collateral to 18 decimals
 *    inside its CR math (Enosys: 6-dec FXRP/stXRP);
 *  - the FEED absorbs the scaling and returns USD x 10^(36 - collDecimals),
 *    leaving trove collateral in raw token units (Ebisu: 8-dec WBTC/LBTC,
 *    6-dec XAUt0/syrupUSDT).
 * Both satisfy `collRaw * price / 1e18 = USD_WAD`, so no on-chain read tells
 * them apart — `branch.priceDecimals` pins it per branch when metadata has it.
 *
 * Absent metadata, auto-detect: the two candidates differ by
 * 10^(18 - collDecimals) (>= 1e10 on every listed branch), so at most one can
 * land in a sane USD band. Prefer the 1e18 reading, fall back to the
 * decimals-adjusted one only when 1e18 is implausibly large, and return 0
 * (unknown -> caller falls back to the shared price map) if neither fits.
 */
export function liquityCollateralPrice(
  raw: bigint | null | undefined,
  collDecimals: number,
  priceDecimals?: number,
): number {
  if (raw == null || raw <= 0n) return 0
  if (priceDecimals != null) return Number(raw) / 10 ** priceDecimals

  const plain = Number(raw) / WAD
  if (collDecimals >= 18 || plain <= MAX_PLAUSIBLE_UNIT_USD) return plain

  const adjusted = Number(raw) / 10 ** (36 - collDecimals)
  return adjusted <= MAX_PLAUSIBLE_UNIT_USD ? adjusted : 0
}

function toHuman(raw: bigint, decimals: number): number {
  return Number(raw) / 10 ** decimals
}

function currencyFor(
  address: string,
  decimals: number,
  symbol: string,
  tokens: GenericTokenList,
): any {
  const lower = address.toLowerCase()
  return tokens[lower] ?? { address: lower, symbol, name: symbol, decimals }
}

/**
 * Map one deployment's on-chain branch batch into the shared
 * `MorphoGeneralPublicResponse` shape (identical to Midnight/Term/Exactly),
 * keyed by `<LENDER>_<chainId>_<COLL_INDEX>` — one key per collateral branch.
 *
 * Per branch:
 *  - the COLLATERAL entry (branch coll token): totals = branch collateral;
 *    LTV = 1/MCR (a trove is liquidatable below ICR = MCR), liquidation
 *    penalty = the SP-offset penalty; borrowing disabled;
 *  - the LOAN entry (the deployment's stable token): `totalDebt` = branch
 *    debt (incl. accrued interest), `totalDeposits` = the branch Stability
 *    Pool ("earn" side), `depositRate` = SP APR (spYieldSplit × Σ debt·rate /
 *    SP size), `variableBorrowRate` = branch average user-set rate. The
 *    ACTUAL borrow rate is per-trove user-set — bounds + averages live in
 *    `params.market` (`minAnnualInterestRate` / `maxAnnualInterestRate` /
 *    `avgBorrowRate`, WAD strings). BOLD-side deposits do NOT collateralize.
 *  - `borrowLiquidity` = mintable headroom: collateral value / MCR − debt,
 *    additionally capped by the branch debt cap on forks that have one.
 *
 * All fork deviations ride in from metadata via `raw.config` / `raw.branch` —
 * nothing here is deployment-specific.
 */
export function convertLiquityMarketsToResponse(
  raw: LiquityMarketsRaw,
  chainId: string,
  prices: { [asset: string]: number } = {},
  _additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokens: GenericTokenList = {},
): { [m: string]: MorphoGeneralPublicResponse } {
  const out: { [m: string]: MorphoGeneralPublicResponse } = {}
  const cfg = raw?.config
  if (!cfg) return out

  const stableAddr = cfg.boldToken.toLowerCase()
  const stableDecimals = cfg.boldDecimals ?? 18
  const stableToken = tokens[stableAddr]
  const stableSymbol = stableToken?.symbol ?? 'BOLD'
  const stablePriceKey =
    toOracleKey(stableToken?.assetGroup!) ||
    toGenericPriceKey(stableAddr, chainId)
  // The stable is redeemable at par — peg to 1 USD unless the shared price
  // map knows better.
  const stablePrice = prices[stablePriceKey] || 1
  const spYieldSplit = cfg.spYieldSplit ? Number(cfg.spYieldSplit) / WAD : 0.75

  for (const b of raw.branches ?? []) {
    const branch = b.branch
    const lenderKey = liquityLenderKey(raw.lender, chainId, branch.collIndex)
    const collAddr = branch.collToken.toLowerCase()
    const collDecimals = branch.collDecimals
    const collToken = tokens[collAddr]
    // Metadata's branch.name is already "STABLE / COLL" — prefer it for the
    // market name; the token-list symbol only labels the collateral rows.
    const collSymbol =
      collToken?.symbol ?? branch.name?.split(' / ').pop() ?? 'COLL'
    const marketName = branch.name ?? `${stableSymbol} / ${collSymbol}`

    const collPriceKey =
      toOracleKey(collToken?.assetGroup!) ||
      toGenericPriceKey(collAddr, chainId)
    const collPrice =
      liquityCollateralPrice(b.collPrice, collDecimals, branch.priceDecimals) ||
      (prices[collPriceKey] ?? 0)

    const entireDebt =
      b.entireDebt !== null ? toHuman(b.entireDebt, stableDecimals) : 0
    const entireColl =
      b.entireColl !== null ? toHuman(b.entireColl, collDecimals) : 0
    const spDeposits =
      b.spDeposits !== null ? toHuman(b.spDeposits, stableDecimals) : 0
    const shutdown = b.shutdownTime !== null && b.shutdownTime > 0n

    // Branch average user-set rate: Σ debt·rate / Σ debt (both from ActivePool,
    // 1e36 / 1e18 scale) → WAD fraction.
    const avgRate =
      b.aggWeightedDebtSum !== null &&
      b.aggRecordedDebt !== null &&
      b.aggRecordedDebt > 0n
        ? Number(b.aggWeightedDebtSum / b.aggRecordedDebt) / WAD
        : 0
    // Annual interest volume (stable units) = Σ debt·rate / 1e36.
    const annualInterest =
      b.aggWeightedDebtSum !== null
        ? Number(b.aggWeightedDebtSum) / WAD / 10 ** stableDecimals
        : 0
    const spApr =
      spDeposits > 0 ? (spYieldSplit * annualInterest) / spDeposits : 0

    const mcr = Number(branch.mcr) / WAD || 1.1
    const ltv = 1 / mcr
    const liqPenalty = branch.liquidationPenaltySP
      ? Number(branch.liquidationPenaltySP) / WAD
      : 0.05

    // Mintable headroom: what the branch collateral could still back at MCR,
    // further capped by the fork's branch debt cap when one exists.
    let borrowLiquidity = Math.max(
      0,
      (entireColl * collPrice) / mcr / stablePrice - entireDebt,
    )
    if (branch.debtCap) {
      const cap = Number(branch.debtCap) / 10 ** stableDecimals
      borrowLiquidity = Math.min(borrowLiquidity, Math.max(0, cap - entireDebt))
    }
    if (shutdown) borrowLiquidity = 0

    /**
     * Rate bounds and adjustment friction come straight from the branch config
     * — the same values already mirrored into `params.market`. A WAD fraction
     * here matches what `modes[troveId]` carries.
     */
    const rateParameter: OpenParameter = {
      kind: 'interest-rate',
      dimension: 'rate',
      domain: {
        min: Number(cfg.minAnnualInterestRate ?? 0) / 1e18,
        max: Number(cfg.maxAnnualInterestRate ?? 0) / 1e18,
      },
      // No protocol default — the borrower must choose. The low end is the
      // cheapest but the first to be redeemed against.
      default: Number(cfg.minAnnualInterestRate ?? 0) / 1e18,
      immutableAfterOpen: false,
      adjustCooldownSeconds: cfg.interestRateAdjCooldownSeconds
        ? Number(cfg.interestRateAdjCooldownSeconds)
        : undefined,
    }

    // @ts-ignore — built incrementally, matches Midnight/Term/Exactly converters
    const entry: MorphoGeneralPublicResponse = { data: {} }

    // 1. COLLATERAL entry — the branch collateral token. Deposit-only.
    const collUid = createMarketUid(chainId, lenderKey, collAddr)
    entry.data[collUid] = {
      marketUid: collUid,
      name: 'Collateral ' + collSymbol,
      poolId: branch.troveManager.toLowerCase(),
      underlying: collAddr,
      asset: currencyFor(collAddr, collDecimals, collSymbol, tokens),
      totalDeposits: entireColl,
      totalDebtStable: 0,
      totalDebt: 0,
      totalLiquidity: entireColl,
      borrowLiquidity: 0,
      totalLiquidityUSD: entireColl * collPrice,
      borrowLiquidityUSD: 0,
      totalDepositsUSD: entireColl * collPrice,
      totalDebtStableUSD: 0,
      totalDebtUSD: 0,
      utilization: 0,
      depositRate: 0,
      variableBorrowRate: 0,
      stableBorrowRate: 0,
      intrinsicYield: 0,
      rewards: undefined,
      decimals: collDecimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor: ltv,
          collateralFactor: ltv,
          borrowFactor: 1,
          liquidationPenalty: liqPenalty,
          closeFactor: 1,
          collateralDisabled: false,
          debtDisabled: true,
          /**
           * Liquity's borrower picks their own interest rate at open, and it
           * sets redemption priority as well as cost — so it is a real
           * parameter, not a quoted rate.
           *
           * The MUTABLE case: adjustable in place, but rate-limited by
           * `interestRateAdjCooldownSeconds` and fee-charged if adjusted
           * early. The per-trove value lives in `modes[troveId]`, because one
           * address can hold several troves at different rates at once.
           */
          openParameter: rateParameter,
        },
      },
      closeFactor: 1,
      collateralActive: true,
      borrowingEnabled: false,
      depositsEnabled: !shutdown,
      hasStable: false,
      isActive: !shutdown,
      isFrozen: shutdown,
    } as any

    // 2. LOAN entry — the deployment's stable token. Debt = branch debt;
    // deposits = the Stability Pool (earn side; NOT collateral).
    const loanUid = createMarketUid(chainId, lenderKey, stableAddr)
    entry.data[loanUid] = {
      marketUid: loanUid,
      name: stableSymbol,
      poolId: branch.stabilityPool.toLowerCase(),
      underlying: stableAddr,
      asset: currencyFor(stableAddr, stableDecimals, stableSymbol, tokens),
      totalDeposits: spDeposits,
      totalDebtStable: 0,
      totalDebt: entireDebt,
      totalLiquidity: borrowLiquidity,
      borrowLiquidity,
      totalLiquidityUSD: borrowLiquidity * stablePrice,
      borrowLiquidityUSD: borrowLiquidity * stablePrice,
      totalDepositsUSD: spDeposits * stablePrice,
      totalDebtStableUSD: 0,
      totalDebtUSD: entireDebt * stablePrice,
      // Share of branch debt covered by its own Stability Pool.
      utilization: spDeposits > 0 ? Math.min(entireDebt / spDeposits, 1) : 0,
      depositRate: spApr * 100,
      variableBorrowRate: avgRate * 100,
      stableBorrowRate: 0,
      // Borrower picks the per-trove rate; the one-off upfront fee is the
      // branch avg rate applied over the upfront period (7d vanilla), as a
      // percent of the borrowed amount.
      rateModel: 'userSet',
      originationFee:
        avgRate *
        (Number(cfg.upfrontInterestPeriodSeconds ?? 604800) / 31_536_000) *
        100,
      intrinsicYield: 0,
      rewards: undefined,
      decimals: stableDecimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor: 0,
          collateralFactor: 0,
          borrowFactor: 1,
          liquidationPenalty: liqPenalty,
          closeFactor: 1,
          // SP deposits earn yield but never collateralize.
          collateralDisabled: true,
          debtDisabled: shutdown,
        },
      },
      closeFactor: 1,
      collateralActive: false,
      borrowingEnabled: !shutdown,
      depositsEnabled: !shutdown,
      hasStable: false,
      variableBorrowDisabled: shutdown,
      isActive: !shutdown,
      isFrozen: shutdown,
    } as any

    entry.params = {
      market: {
        lender: lenderKey,
        name: marketName,
        loanDecimals: stableDecimals,
        collateralDecimals: collDecimals,
        // TroveManager doubles as the branch/market id (marketUid address).
        id: branch.troveManager.toLowerCase(),
        lltv: branch.mcr,
        oracle: branch.priceFeed ?? zeroAddress,
        irm: zeroAddress,
        collateralAddress: collAddr,
        loanAddress: stableAddr,
        // --- Liquity-family descriptor (all values from metadata/on-chain;
        // consumed by calldata builders + worker-api resolvers) ---
        liquity: {
          collIndex: branch.collIndex,
          userSetRate: true,
          redemptionOrdered: true,
          minDebt: cfg.minDebt,
          minAnnualInterestRate: cfg.minAnnualInterestRate,
          maxAnnualInterestRate: cfg.maxAnnualInterestRate,
          avgBorrowRate: String(Math.round(avgRate * WAD)),
          gasCompensation: cfg.gasCompensation,
          upfrontInterestPeriodSeconds: cfg.upfrontInterestPeriodSeconds,
          interestRateAdjCooldownSeconds: cfg.interestRateAdjCooldownSeconds,
          ccr: branch.ccr,
          mcr: branch.mcr,
          scr: branch.scr,
          bcr: branch.bcr,
          liquidationPenaltySP: branch.liquidationPenaltySP,
          liquidationPenaltyRedistribution:
            branch.liquidationPenaltyRedistribution,
          debtCap: branch.debtCap ?? undefined,
          collWrapper: branch.collWrapper ?? undefined,
          shutdown,
          troveCount: b.troveCount !== null ? Number(b.troveCount) : undefined,
          addresses: {
            borrowerOperations: branch.borrowerOperations,
            troveManager: branch.troveManager,
            troveNFT: branch.troveNFT,
            stabilityPool: branch.stabilityPool,
            sortedTroves: branch.sortedTroves,
            priceFeed: branch.priceFeed,
            collSurplusPool: branch.collSurplusPool,
            collateralRegistry: cfg.collateralRegistry,
            hintHelpers: cfg.hintHelpers,
            multiTroveGetter: cfg.multiTroveGetter,
            wethToken: cfg.wethToken,
          },
        },
      } as any,
    }
    entry.chainId = chainId

    out[lenderKey] = entry
  }

  return out
}
